import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface IndexPickerRowProps {
  /** Official Morningstar index name. */
  name: string;
  /** Coverage/scope label shown first in the meta line (e.g. "United States"). */
  coverageLabel: string;
  /** Distinct-country count; hidden until known. */
  countries?: number;
  /** Constituent count; hidden until measured. */
  constituents?: number;
  provider?: string;
  /** Shows the leading check. Universe picker is single-select; the benchmark
   *  picker is multi-select and removes a chosen index from the list, so it
   *  always passes false (the check column stays reserved so both read
   *  identically). */
  selected?: boolean;
}

// The row shared by the universe index picker (UniverseSelector) and the
// benchmark picker (BenchmarkSearchPopover) so the two dropdowns are literally
// the same element: leading check (reserved even when hidden, for alignment),
// the index name, a "coverage • N Countries • N Constituents" meta line, and a
// right-aligned violet provider label + Morningstar logo. Neither shows a code
// badge — the benchmark code is only rendered on the selected chips below.
export function IndexPickerRow({
  name,
  coverageLabel,
  countries,
  constituents,
  provider = 'Morningstar',
  selected = false,
}: IndexPickerRowProps) {
  return (
    <div className="flex items-center gap-3 w-full">
      <Check
        className={cn(
          'h-4 w-4 text-primary flex-shrink-0',
          selected ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div className="flex flex-col min-w-0 flex-1">
        <span className="font-medium text-[15px] leading-none truncate">
          {name}
        </span>
        <span className="flex items-center gap-2 text-[12px] text-muted-foreground mt-1.5">
          <span className="text-[13px]">{coverageLabel}</span>
          {countries != null && (
            <>
              <span className="opacity-30">•</span>
              <span className="text-[13px]">
                {countries} {countries === 1 ? 'Country' : 'Countries'}
              </span>
            </>
          )}
          {constituents != null && (
            <>
              <span className="opacity-30">•</span>
              <span className="text-[13px]">
                {constituents.toLocaleString()} Constituents
              </span>
            </>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
        <span className="text-[14px] font-medium text-primary">{provider}</span>
        <img
          src="/morningstar_logo.png"
          alt={provider}
          className="w-7 h-7 rounded object-cover shadow-sm border border-border/10"
        />
      </div>
    </div>
  );
}
